import { createNonAuthedClient, mapRespData, patchClient } from "./patchClient";
import type { App as AppUid } from "@/modules/app";
import {
  appServiceDeleteInstalledApp,
  appServiceListInstalledApps,
  type deputy_v1_app_InstalledApp,
  type deputy_v1_app_App,
  appServiceCreateInstalledApp,
  appServiceGetInstalledApp,
  appServiceUpdateInstalledApp,
  appServiceInvokeAppRpcPostHook,
  type deputy_v1_app_InvokeAppRPCPostHookRequest,
} from "@deputy-ai/data-platform-api/v1/app";
import { appServiceListApps } from "@deputy-ai/data-platform-api/v1/app";
import type { Options, RequestResult } from "@hey-api/client-fetch";
import type { Merge } from "type-fest";

export type App = deputy_v1_app_App;
export type InstalledApp = Merge<
  deputy_v1_app_InstalledApp,
  { appUid: AppUid }
>;
export type AppInvokeRpcHookBody =
  deputy_v1_app_InvokeAppRPCPostHookRequest["body"];

export const getAppListApi = patchClient(appServiceListApps);

export const sortInstalledApps = (apps: InstalledApp[]) =>
  apps.sort((a, b) => {
    const aOrder = a.order ?? 0;
    const bOrder = b.order ?? 0;
    if (aOrder !== bOrder) {
      return bOrder - aOrder;
    }
    return a.displayName.localeCompare(b.displayName);
  });

export const getInstalledAppListApi = patchClient(appServiceListInstalledApps, {
  mapResp: (resp) =>
    mapRespData(resp, (res) => {
      let apps = res?.apps as InstalledApp[] | undefined;
      apps &&= sortInstalledApps(apps);
      return {
        ...res,
        apps,
      };
    }),
});
export const getInstalledAppApi = patchClient(appServiceGetInstalledApp, {
  mapResp: (resp) =>
    mapRespData(resp, (res) => ({
      ...res,
      app: res?.app as InstalledApp | undefined,
    })),
});
export const deleteInstalledAppApi = patchClient(appServiceDeleteInstalledApp);
export const updateInstalledAppApi = patchClient(appServiceUpdateInstalledApp);

export const createInstallAppApi = patchClient(appServiceCreateInstalledApp, {
  mapResp: (resp) =>
    mapRespData(resp, (res) => ({
      ...res,
      app: res?.app as InstalledApp | undefined,
    })),
});
export const invokeRpcHookApi = patchClient(appServiceInvokeAppRpcPostHook);

export function createAppHookApiCreator(app: string) {
  return function appHookApiCreator<T = unknown>(url: string) {
    return async function AppHookApi({
      installedAppUid,
      body,
      ...options
    }: {
      installedAppUid: string;
    } & Options<T>) {
      const client = await createNonAuthedClient();

      return await client.post({
        url: `/v1/apps/${app}/hooks/${url}`,
        body: {
          installedAppUid,
          body: body ?? {},
        },
        ...options,
      });
    };
  };
}

const invokeAppRpcPostHookApi = patchClient(appServiceInvokeAppRpcPostHook);

export interface InvokePostHookRequest<T extends Record<string, unknown>> {
  installedAppUid: string;
  body?: T;
}

export function createInvokeAppRpcPostHookApi<
  Req extends Record<string, unknown>,
  Resp extends Record<string, unknown>,
>(appUid: string, handle: string) {
  return async function (
    req: Options<InvokePostHookRequest<Req>>
  ): RequestResult<Resp> {
    const resp = await invokeAppRpcPostHookApi({
      path: {
        appUid,
        handle,
      },
      body: {
        appUid,
        handle,
        ...req,
      },
    });
    if (resp.error) {
      return {
        request: resp.request,
        response: resp.response,
        data: undefined,
        error: resp.error,
      };
    }
    return {
      request: resp.request,
      response: resp.response,
      data: resp.data as Resp,
      error: undefined,
    };
  };
}
